import { jsx as _jsx } from "react/jsx-runtime";
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { MCPServerIcon } from '#pplx/frontend/apps/ask/comet-browser/components/MCPServerIcon.js';
import { findIconUrlForServer, useCometAvailableDxts, } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometAvailableDxts.js';
import { useCometState, useEntropyBrowser, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { extractRequiredPermissionsFromManifest, useCometRequiredSystemPermissions, } from '#pplx/frontend/comet/client/hooks/useCometRequiredSystemPermissions.js';
import { findInstalledManifestForServer } from '#pplx/frontend/comet/client/utils.js';
import { shallowEqual } from '#pplx/frontend/common/shallowEqual.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
const AskPermissionsModal = Dynamic(async () => {
    const { AskPermissionsModal } = await import('#pplx/frontend/apps/ask/comet-browser/components/AskPermissionsModal.js');
    return { default: AskPermissionsModal };
});
export const useCometMcpServers = () => {
    const cometState = useCometState();
    const { openModal } = useModalContext();
    const availableDxts = useCometAvailableDxts();
    const requiredPermissions = useCometRequiredSystemPermissions({
        cometState,
    });
    const cometAdapter = useEntropyBrowser();
    // Use queries is not referencially stable, so extract what we need
    // and do a shallow comparison to avoid unnecessary re-renders.
    const queriesData = useQueries({
        queries: requiredPermissions.map((permission) => ({
            queryKey: QueryClientHelper.makeQueryKey('comet/permission', permission),
            queryFn: () => cometAdapter.hasPermission(permission),
        })),
    }).map((query) => query.data);
    const getQueriesData = useMemo(() => {
        let curData;
        return (newData) => {
            if (!shallowEqual(curData, newData)) {
                curData = newData;
            }
            return curData;
        };
    }, []);
    const stableQueriesData = getQueriesData(queriesData);
    const serverNamesWithoutRequiredPermissions = useMemo(() => {
        return new Set(cometState.mcpStdioServers
            ?.filter((server) => {
            const installedManifest = findInstalledManifestForServer(cometState.installedDxts, server);
            const permissions = installedManifest
                ? extractRequiredPermissionsFromManifest(installedManifest)
                : [];
            const hasNotGrantedPermissions = permissions.some((permission) => {
                const index = requiredPermissions.indexOf(permission);
                return index !== -1 && stableQueriesData[index] === false;
            });
            return hasNotGrantedPermissions;
        })
            .map((server) => server.name));
    }, [
        cometState.mcpStdioServers,
        cometState.installedDxts,
        requiredPermissions,
        stableQueriesData,
    ]);
    const cometMcpSources = useMemo(() => Object.fromEntries(cometState.mcpStdioServers?.map((server) => {
        const installedManifest = findInstalledManifestForServer(cometState.installedDxts, server);
        const iconUrl = findIconUrlForServer(availableDxts, server);
        const displayName = installedManifest?.display_name ?? installedManifest?.name ?? server.name;
        const hasNotGrantedPermissions = serverNamesWithoutRequiredPermissions.has(server.name);
        return [
            server.name,
            {
                icon: undefined,
                avatar: _jsx(MCPServerIcon, { serverName: displayName, iconUrl: iconUrl, size: "sm" }),
                text: displayName,
                disabled: server.status !== 'running' || !server.tools_count || hasNotGrantedPermissions,
                description: '',
                type: 'toggle',
                iconUrl,
            },
        ];
    }) ?? []), [
        cometState.mcpStdioServers,
        cometState.installedDxts,
        availableDxts,
        serverNamesWithoutRequiredPermissions,
    ]);
    const executeWithPermissionCheck = useCallback((serverName, action) => {
        if (!serverNamesWithoutRequiredPermissions.has(serverName)) {
            action();
            return;
        }
        const server = cometState.mcpStdioServers?.find((s) => s.name === serverName);
        if (!server)
            return;
        const installedManifest = findInstalledManifestForServer(cometState.installedDxts, server);
        if (!installedManifest)
            return;
        openModal(AskPermissionsModal, {
            serverName,
            permissions: extractRequiredPermissionsFromManifest(installedManifest),
            onPermissionsGranted: action,
        }, { replace: true });
    }, [
        cometState.mcpStdioServers,
        cometState.installedDxts,
        serverNamesWithoutRequiredPermissions,
        openModal,
    ]);
    const setCometMcpSources = useCallback((serverNames) => {
        // create a new set of all the possible servers
        const nextDisabledServers = new Set(cometState.mcpStdioServers?.map((server) => server.name) ?? []);
        // remove the servers that are in the serverNames array
        serverNames.forEach((serverName) => nextDisabledServers.delete(serverName));
        // set the new disabled servers
        cometState.actions.setMcpDisabledServers(nextDisabledServers);
    }, [cometState.actions, cometState.mcpStdioServers]);
    const toggleCometMcpSource = useCallback((serverName) => {
        const toggleServer = () => {
            const disabledServers = cometState.mcpDisabledServers ?? new Set();
            const nextDisabledServers = new Set(disabledServers);
            if (disabledServers.has(serverName)) {
                nextDisabledServers.delete(serverName);
            }
            else {
                nextDisabledServers.add(serverName);
            }
            cometState.actions.setMcpDisabledServers(nextDisabledServers);
        };
        executeWithPermissionCheck(serverName, toggleServer);
    }, [cometState.actions, cometState.mcpDisabledServers, executeWithPermissionCheck]);
    const addCometMcpSource = useCallback((serverName) => {
        const enableServer = () => {
            const disabledServers = cometState.mcpDisabledServers ?? new Set();
            const nextDisabledServers = new Set(disabledServers);
            nextDisabledServers.delete(serverName);
            cometState.actions.setMcpDisabledServers(nextDisabledServers);
        };
        executeWithPermissionCheck(serverName, enableServer);
    }, [cometState.actions, cometState.mcpDisabledServers, executeWithPermissionCheck]);
    const removeCometMcpSource = useCallback((serverName) => {
        const disabledServers = cometState.mcpDisabledServers ?? new Set();
        const nextDisabledServers = new Set(disabledServers);
        nextDisabledServers.add(serverName);
        cometState.actions.setMcpDisabledServers(nextDisabledServers);
    }, [cometState.actions, cometState.mcpDisabledServers]);
    const cometMcpDisabledServers = useMemo(() => new Set([...(cometState.mcpDisabledServers ?? []), ...serverNamesWithoutRequiredPermissions]), [cometState.mcpDisabledServers, serverNamesWithoutRequiredPermissions]);
    const hasCometMcpSource = useCallback((serverName) => serverName in cometMcpSources && !cometMcpDisabledServers?.has(serverName), [cometMcpSources, cometMcpDisabledServers]);
    return useMemo(() => ({
        cometMcpSources,
        setCometMcpSources,
        toggleCometMcpSource,
        addCometMcpSource,
        removeCometMcpSource,
        hasCometMcpSource,
        cometMcpDisabledServers,
    }), [
        cometMcpSources,
        setCometMcpSources,
        toggleCometMcpSource,
        addCometMcpSource,
        removeCometMcpSource,
        hasCometMcpSource,
        cometMcpDisabledServers,
    ]);
};
//# sourceMappingURL=useCometMcpServers.js.map